import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Mail, Loader2, Crown, UserMinus, X, LogOut, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Team() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/team");
      setTeam(res.data);
    } catch (e) {
      toast.error("Failed to load team");
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team/invite", { email, origin_url: window.location.origin });
      toast.success(`Invitation emailed to ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invite");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    await api.delete(`/team/invites/${id}`);
    toast.success("Invitation revoked");
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from your team?`)) return;
    await api.delete(`/team/members/${m.user_id}`);
    toast.success("Teammate removed");
    load();
  };

  const leave = async () => {
    if (!window.confirm("Leave this team? You'll go back to your own vault.")) return;
    await api.post("/team/leave");
    await refreshUser();
    toast.success("You left the team");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 py-8">
        <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#0F172A] mb-5">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0F172A]">Team</h1>
        <p className="text-sm text-[#64748B] mt-1">Invite coworkers to share one authorization vault and plan capacity.</p>

        {!team ? (
          <div className="p-16 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-[#E2E8F0] border-t-[#2563EB] animate-spin" /></div>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid="team-vault-card">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#64748B]"><Users size={14} /> Shared vault</div>
              <p className="mt-1 text-lg font-bold text-[#0F172A]" data-testid="team-vault-name">{team.vault_name || "Your vault"}</p>
              {team.is_owner ? (
                <form onSubmit={invite} className="mt-4 flex gap-2 flex-wrap" data-testid="team-invite-form">
                  <input data-testid="team-invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coworker@business.com"
                    className="flex-1 min-w-[220px] rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10" />
                  <button data-testid="team-invite-submit-btn" disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Send invite
                  </button>
                </form>
              ) : (
                <div className="mt-4 flex items-center justify-between gap-3 flex-wrap rounded-lg bg-[#F0F9FF] p-3">
                  <p className="text-xs text-[#0284C7]">You're a member of this team. Billing and capacity are managed by the owner.</p>
                  <button data-testid="team-leave-btn" onClick={leave} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs font-semibold text-[#DC2626] hover:bg-red-50"><LogOut size={13} /> Leave team</button>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-[#E2E8F0] text-xs font-semibold uppercase tracking-wider text-[#64748B]">Members</div>
              <MemberRow m={team.owner} icon={<Crown size={13} className="text-[#D97706]" />} />
              {team.members.map((m) => (
                <MemberRow key={m.user_id} m={m} action={team.is_owner && (
                  <button data-testid={`team-remove-${m.user_id}`} onClick={() => remove(m)} title="Remove" className="rounded-lg p-2 text-[#94A3B8] hover:text-[#DC2626] hover:bg-red-50"><UserMinus size={15} /></button>
                )} />
              ))}
              {team.members.length === 0 && <p className="px-5 py-4 text-sm text-[#94A3B8]" data-testid="team-no-members">No teammates yet.</p>}
            </div>

            {team.is_owner && (
              <div className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="team-invites-card">
                <div className="px-5 py-3 border-b border-[#E2E8F0] text-xs font-semibold uppercase tracking-wider text-[#64748B]">Pending invitations</div>
                {team.invites.length === 0 && <p className="px-5 py-4 text-sm text-[#94A3B8]">No pending invitations.</p>}
                {team.invites.map((i) => (
                  <div key={i.invite_id} data-testid={`team-invite-${i.invite_id}`} className="px-5 py-3 flex items-center justify-between border-b border-[#F1F5F9] last:border-0">
                    <div className="flex items-center gap-2 text-sm text-[#0F172A]"><Clock size={14} className="text-[#D97706]" /> {i.email}</div>
                    <button data-testid={`team-revoke-${i.invite_id}`} onClick={() => revoke(i.invite_id)} className="rounded-lg p-2 text-[#94A3B8] hover:text-[#DC2626] hover:bg-red-50"><X size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

const MemberRow = ({ m, icon, action }) => (
  <div className="px-5 py-3 flex items-center justify-between border-b border-[#F1F5F9] last:border-0" data-testid={`team-member-${m.user_id}`}>
    <div className="flex items-center gap-3">
      <span className="h-8 w-8 rounded-full bg-[#EFF6FF] text-[#2563EB] text-xs font-bold flex items-center justify-center">{(m.name || m.email)[0]?.toUpperCase()}</span>
      <div>
        <p className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">{m.name || m.email} {icon}</p>
        <p className="text-xs text-[#64748B]">{m.email} · <span className="capitalize">{m.role}</span></p>
      </div>
    </div>
    {action}
  </div>
);
